import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import ValidationErrorMessage from './ValidationErrorMessage';

/**
 * Component to return the associated amount radio and other amount fields.
 *
 * @params see propTypes.
 * @returns {JSX.Element}
 * @component
 */
const DonationAmount = ({ calculateGiftAid, formConfig }) => {
  /**
   * Deconstructs react hook form methods from parent form.
   */
  const {
    register,
    getValues,
    setValue,
    formState: { errors }
  } = useFormContext();

  /**
   * Minimum donation amount permitted
   * @type {number}
   */
  const minimumDonation = 5;

  /**
   * Other amount ref, used to target element,
   * @type {React.MutableRefObject<null>}
   */
  const otherAmountRef = useRef(null);

  /**
   * Update the actual AMOUNT value when either the amount_radio or other_amount inputs are changed.
   * @param event
   */
  const onAmountChange = (event) => {
    const input = event.currentTarget;

    if (!input) {
      return;
    }

    const isOtherAmountWrapper = input.id === 'other-amount-wrapper';
    const isOtherAmountInput = event.target.id === 'other_amount';

    if (isOtherAmountWrapper || isOtherAmountInput) {
      const otherRadioButton = document.querySelector('input[name="amount_radio"][value="other"]');
      otherRadioButton.checked = true;
      otherAmountRef?.current?.focus();
    } else if (otherAmountRef?.current && event.target !== otherAmountRef?.current) {
      otherAmountRef.current.value = '';
    }

    setValue('AMOUNT', isOtherAmountWrapper || isOtherAmountInput ? otherAmountRef?.current?.value ?? '' : event.target.value);
    setValue('amount_radio', isOtherAmountWrapper || isOtherAmountInput ? 'other' : event.target.value);
    setValue('other_amount', isOtherAmountWrapper || isOtherAmountInput ? otherAmountRef?.current?.value ?? '' : '');
    calculateGiftAid();
  };

  /**
   * Restrict the donation amount to integer.
   * @param event
   */
  const restrictAsCurrency = (event) => {
    const currencyPattern = '^\\d*(\\.\\d{0,2})?$';
    const currencyRegex = new RegExp(currencyPattern, 'g');

    const otherAmountElement = event.target;
    let cursorPosition = otherAmountElement.selectionStart;

    if (!currencyRegex.test(otherAmountElement.value)) {
      cursorPosition--;
      otherAmountElement.value = otherAmountElement.value.slice(0, otherAmountElement.value.length - 1);
    }

    otherAmountElement.setSelectionRange(cursorPosition, cursorPosition);
  };

  /**
   * Other amount registration function. Assigned to the input.
   */

  // eslint-disable-next-line no-unused-vars
  const { ref: refOtherAmount, ...restOtherAmount } = register('other_amount', {
    validate: {
      required: (value) => {
        if (
          getValues('amount_radio') === 'other' &&
            (!value || value < minimumDonation)
        ) {
          return `Minimum donation amount is £${minimumDonation.toString()}`;
        }
      }
    },
    onChange: (e) => {
      restrictAsCurrency(e);
      onAmountChange(e);
    }
  });

  const labelKeyHandler = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.currentTarget.click();
      e.currentTarget.focus();
    }
  };

  return (
    <fieldset
      className="radio-group mt-12"
      id="amount_radio">
      <legend
        id="amount-legend"
        className="m-0 fs-6 fw-normal float-none">
        Select the amount you would like to donate
      </legend>

      {
        (errors.amount_radio || errors.other_amount) &&
        <div className="mt-2">
          {
            errors.amount_radio &&
            <ValidationErrorMessage error={errors.amount_radio}/>
          }
          {

            errors.other_amount &&
            <ValidationErrorMessage error={errors.other_amount}/>
          }
        </div>
      }

      <div
        className={`mt-${(errors.amount_radio || errors.other_amount) ? '2' : '4'} d-flex justify-content-between`}
        role="group"
        aria-labelledby="amount-legend">
        {
          formConfig.PaymentOptionsArray.map(price =>
            <div
              className="c-radio-button d-flex justify-content-center rounded h3 fw-light"
              key={price}>
              <input
                id={`amount-${price}`}
                type="radio"
                className="visually-hidden"
                {...register('amount_radio', {
                  required: 'Select a donation amount',
                  onChange: onAmountChange
                })}
                defaultChecked={price === formConfig.DefaultPaymentAmount}
                value={price}
                tabIndex="-1"/>
              <label
                className="w-100 d-flex justify-content-center align-items-center rounded"
                htmlFor={`amount-${price}`}
                onKeyUp={labelKeyHandler}
                tabIndex="0">
                £{price}
              </label>
            </div>
          )}
      </div>

      <label
        key="other"
        className="visually-hidden">
        <input
          type="radio"
          {...register('amount_radio', {
            required: 'Select a donation amount',
            onChange: onAmountChange
          })}
          value="other"
          tabIndex="-1"/>
        Other
      </label>

      <label
        className="d-block mt-4 fs-6 fw-normal"
        htmlFor="other_amount">
        Or you can specify an amount (minimum £{minimumDonation.toString()})
      </label>

      <div
        id="other-amount-wrapper"
        className="c-radio-button c-radio-button--other-amount-input d-inline-flex justify-content-sm-md-center align-items-center mt-4 px-2 h3 fw-light"
        onClick={onAmountChange}>
        <span>
          £
        </span>
        <input
          id="other_amount"
          className="border-0 p-0 fw-light text-blue-anchor"
          {...register('amount_radio', {
            required: 'Select a donation amount',
            onChange: onAmountChange
          })}
          ref={otherAmountRef}
          {...restOtherAmount}/>
      </div>
    </fieldset>
  );
};

DonationAmount.propTypes = {
  /**
   * The donation form calculateGiftAid state update function.
   */
  calculateGiftAid: PropTypes.func.isRequired,
  /**
   * The form settings returned from the settings api.
   */
  formConfig: PropTypes.object.isRequired

};

export default DonationAmount;
